import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  color,
  Text,
  typography,
  CheckFillSvg,
  Link,
  StyleProps,
  PrimaryButton,
  spacing,
} from 'deepstash-ui';
import { CollectionApiResponse } from 'api/api.types';
import { GAP_SIZE, slugify } from 'utils/global';
import NextImage from 'src/components/image/NextImage';
import { useRouter } from 'next/router'; // Use it instead of own useRouter for fixing scroll problems
import NextLink from 'src/components/navigation/NextLink';

const COLLECTION_IMAGE_SIZE = { WIDTH: 140, HEIGHT: 175, BORDER_RADIUS: 8 };
const FALLBACK_SKILLS = [
  'How to prioritize tasks effectively',
  'How to minimize distractions',
  'How to optimize your work environment',
  'How to use technology to your advantage',
  'How to delegate tasks efficiently',
];

interface CollectionInHeaderProps {
  collection: CollectionApiResponse;
  topic: string;
  showStartJourneyButton?: boolean;
}

const CollectionInHeader: React.FC<CollectionInHeaderProps & StyleProps> = ({
  collection,
  topic,
  showStartJourneyButton = false,
  ...props
}) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();
  const router = useRouter();

  const collectionSlug = React.useMemo(() => {
    return slugify(collection.name);
  }, [collection.name]);

  const collectionPageRoute = `/collection/${collection.id}/${collectionSlug}`;

  const finalSkills = React.useMemo(() => {
    return collection.skills !== undefined
      ? collection.skills
      : FALLBACK_SKILLS;
  }, [collection.skills]);

  const renderSkill = ({ skill, index }: { skill: string; index: number }) => {
    return (
      <Flex
        key={`skill-${index}`}
        flexDirection="row"
        alignItems="flex-start"
        gridColumnGap={4}
      >
        <CheckFillSvg
          color={'green'}
          boxSize={isMobileView ? '24px' : '26px'}
        />
        <Text
          {...(isMobileView
            ? typography.primaryTextFonts.large.regular
            : typography.primaryTextFonts.large.regular)}
          color={color[colorMode].textSecondary}
          flex={1}
        >
          {skill}
        </Text>
      </Flex>
    );
  };

  const collectionImage = (
    <NextImage
      wrapperStyle={{
        width: COLLECTION_IMAGE_SIZE.WIDTH,
        height: COLLECTION_IMAGE_SIZE.HEIGHT,
        borderRadius: COLLECTION_IMAGE_SIZE.BORDER_RADIUS,
        cursor: 'pointer',
      }}
      imageUrl={collection.image}
      blurhash={collection.image_blurhash}
      alt={`${collection.name}`}
      priority={false}
      onClick={() => {
        router.push(collectionPageRoute);
      }}
    />
  );

  return (
    <Flex
      bgColor={`${color[colorMode].top}66`}
      flexDirection={isMobileView ? 'column' : 'row'}
      p={GAP_SIZE}
      gridGap={GAP_SIZE}
      borderRadius={16}
      alignItems={'flex-end'}
      {...props}
    >
      {isMobileView ? (
        <Flex width="100%" justifyContent="center">
          {collectionImage}
        </Flex>
      ) : (
        collectionImage
      )}
      <Flex flex={1} flexDirection={'column'}>
        <Text
          {...typography.primaryTextFonts.large.regular}
          color={color[colorMode].text}
        >
          {`Learn more about ${topic} with this collection`}
        </Text>
        <NextLink href={collectionPageRoute} passHref>
          <Link _focus={undefined} _hover={{ textDecoration: 'underline' }}>
            <Text
              {...typography.primaryHeadingFonts.medium.bold}
              color={color[colorMode].text}
            >
              {collection.name}
            </Text>
          </Link>
        </NextLink>
        <Flex
          flexDirection="column"
          gridRowGap={8}
          mt={16}
          {...(isMobileView ? { width: '100%' } : {})}
        >
          {finalSkills.slice(0, 3).map((skill, index) => {
            return renderSkill({ skill, index });
          })}
        </Flex>
      </Flex>
      {showStartJourneyButton && (
        <PrimaryButton
          backgroundColor={color[colorMode].text}
          height={spacing.toRem(48)}
          width={{ base: '100%', md: 'auto' }}
          onClick={() => router.push(collectionPageRoute)}
        >
          Start Journey
        </PrimaryButton>
      )}
    </Flex>
  );
};

export default CollectionInHeader;
